import React from "react";

export const privacyPolicyContent = {
  en: {
    title: "Privacy and Security Policy",
    subtitle: "KinetikOS",
    sections: [
      {
        heading: "Context",
        body: (
          <>
            <p>
              KINETIKOS - DRIVEN SOLUTIONS, S.A. (hereinafter Kinetikos) is a company under
              Portuguese law that developed KinetikOS, a cloud-based software that functions as a
              biomechanical measurement instrument. KinetikOS receives inertial or kinematic signals
              previously acquired by third-party systems (external inertial measurement units,
              smartphone and smartwatch IMUs, optoelectronic and laboratory electromagnetic
              systems) and, through the application of signal processing algorithms and the
              calculation of statistical descriptors, produces mechanical quantities descriptive of
              human movement, expressed in conventional physical units or in dimensionless
              mathematical descriptors.
            </p>
            <p>
              The parameters calculated by KinetikOS are made available, as technical measurement
              data, exclusively through the KinetikOS APIs to third-party applications that
              integrate the device, under a contractual agreement or technical partnership with the
              manufacturer. The function of KinetikOS is merely informative and descriptive.
              KinetikOS does not perform any clinical interpretation, classification, comparison
              with reference values, scoring, alert, recommendation, or clinical judgment regarding
              the calculated values.
            </p>
            <p>
              KinetikOS is intended for researchers in biomechanics and movement sciences, sports
              science professionals, fitness professionals, trainers, individual users in
              self-monitoring contexts, and healthcare professionals who use it exclusively as a
              biomechanical measurement instrument.
            </p>
            <p>
              Given that KinetikOS is made available exclusively through APIs to integrating
              partners, there is, as a rule, no direct interaction between Kinetikos and the end
              users of the third-party applications that integrate the device. The collection,
              processing, and presentation of these end users' personal data are typically the
              responsibility of the integrating partner, as the controller. Within the technical
              scope of providing the service, Kinetikos may access technical measurement data and
              identifiers transmitted by the integrating partner through the APIs, acting, in these
              cases, as a processor for the integrating partner, under the terms of Article 28 of
              Regulation (EU) 2016/679 (GDPR).
            </p>
            <p>
              This privacy and security policy aims to inform all integrating partners, their
              representatives, and, to the extent applicable, end users about how personal data
              potentially processed in the context of KinetikOS is collected, processed, stored,
              and protected, as well as the applicable purposes and retention periods.
            </p>
          </>
        ),
      },
      {
        heading: "Entities Responsible for Processing Your Personal Data",
        body: (
          <>
            <p>
              The entity responsible for processing the personal data of end users of third-party
              applications that integrate KinetikOS is, as a rule, the integrating partner itself,
              who is responsible for defining the purposes and means of processing this data with
              end users, as well as ensuring compliance with the obligations arising from the GDPR,
              particularly regarding legal bases, information, security, and the exercise of
              rights.
            </p>
            <p>
              Within the scope of providing the biomechanical measurement service through the
              KinetikOS APIs, Kinetikos acts as a processor for the integrating partner, receiving
              and processing the transmitted signals and parameters, exclusively under a
              contractual agreement that defines the terms of processing, in accordance with
              Article 28 of the GDPR.
            </p>
            <p>
              Regarding the personal data of representatives, technical users, and contact persons
              of integrating partners (namely data collected for registration, API credential
              provisioning, invoicing, support, and commercial communication purposes), the data
              controller is:
            </p>
            <p>
              <strong>KINETIKOS - DRIVEN SOLUTIONS, S.A.</strong>
              <br />
              Address: Rua Pedro Nunes, Edifício C, 3030-199 Coimbra, Portugal
              <br />
              Email: info@kinetikoshealth.com
              <br />
              Telephone: (+351) 215 836 938
            </p>
          </>
        ),
      },
      {
        heading: "What Personal Data Is Collected",
        body: (
          <>
            <p>
              In the context of its role as a processor for the integrating partner, Kinetikos
              receives the data transmitted by the partner through the KinetikOS APIs. The nature
              and scope of the received data are defined, on a case-by-case basis, in the
              contractual agreement signed with each integrating partner, being limited to what is
              strictly necessary for the provision of the measurement service. These may include,
              namely:
            </p>
            <ul>
              <li>
                Pseudonymized or technical identifiers assigned by the integrating partner to their
                end users (e.g., session identifiers or internal user identifiers), without
                Kinetikos typically having access to the direct identification data of these users.
              </li>
              <li>
                Inertial or kinematic signals previously acquired by third-party systems, which,
                depending on the case and their association with an identified or identifiable
                person, may constitute biometric data within the meaning of the GDPR.
              </li>
              <li>
                Technical anthropometric parameters (height, weight, age, and sex), when
                transmitted by the integrating partner, exclusively to the extent necessary for the
                application of dimensional normalization mathematical models, the estimation of
                body segment inertia, and other biomechanical calculations. These parameters are
                treated as technical inputs for the algorithms and not for demographic
                characterization, comparison with normative values, classification, scoring, or any
                clinical interpretation purposes.
              </li>
              <li>
                Technical metadata associated with API requests, namely timestamps, signal type,
                configuration parameters, and the integrating partner's identifier.
              </li>
            </ul>
            <p>
              Kinetikos does not collect or require direct identification data of end users (such
              as name, date of birth, address, or contacts), clinical data, geolocation data,
              camera data, microphone data, or any other data not strictly necessary for
              calculating the mechanical quantities descriptive of movement. KinetikOS does not
              access the end user's device sensors, camera, or location.
            </p>
            <p>
              In the context of its role as a controller, within the scope of the contractual
              relationship with integrating partners, Kinetikos collects the following personal
              data from their respective representatives and technical users:
            </p>
            <ul>
              <li>
                Identification and contact data - name, position, email address, and, optionally,
                phone number.
              </li>
              <li>
                API authentication and usage data - technical credentials, access, and audit logs.
              </li>
              <li>
                Contractual and invoicing data - information necessary for the conclusion and
                execution of the service provision contract.
              </li>
              <li>
                The personal data collected within the scope of KinetikOS are adequate, relevant,
                and limited to what is necessary for fulfilling the processing purposes, in
                accordance with the data minimization principle set out in Article 5(1)(c) of the
                GDPR.
              </li>
            </ul>
          </>
        ),
      },
      {
        heading: "How We Collect and Access Personal Data",
        body: (
          <>
            <p>
              The data processed within the scope of providing the measurement service is received
              by KinetikOS exclusively through its APIs, by the integrating partner's initiative
              and under their control. Kinetikos does not capture, record, or acquire data directly
              from end users.
            </p>
            <p>
              The personal data of integrating partners' representatives are collected directly by
              Kinetikos at the time of registration, API credential provisioning, or within the
              contractual process, through the completion of forms or formal communications
              between the parties.
            </p>
          </>
        ),
      },
      {
        heading: "For What Purposes Is Personal Data Processed",
        body: (
          <>
            <p>
              Personal data processed within the scope of KinetikOS is processed strictly in
              accordance with the contractual agreement signed with the integrating partner, for
              the following purposes:
            </p>
            <ul>
              <li>
                Provision of the biomechanical measurement service, which consists of processing
                inertial or kinematic signals and producing parameters descriptive of human
                movement, to be made available exclusively to the integrating partner through the
                KinetikOS APIs.
              </li>
              <li>
                Compliance with contractual and legal obligations associated with the provision of
                the service, including access management, audit logging, security management,
                invoicing, and technical support.
              </li>
              <li>
                Technical and operational management of the APIs, including the identification and
                resolution of faults, performance control, and security monitoring, whenever
                technically necessary.
              </li>
              <li>
                Content management, which consists of collecting personal data, subject to the
                recipient's express and informed consent, for receiving newsletters with news
                recorded in the activity developed by Kinetikos and in other services provided by
                it.
              </li>
            </ul>
            <p>
              Kinetikos does not interpret, classify, compare with reference values, score, alert,
              recommend, or issue any clinical judgment on the processed data. Such activities, if
              carried out by the integrating partner or third parties, are their sole
              responsibility and constitute downstream use, outside the intended purpose declared
              by the manufacturer, under the terms of Article 2(12) of Regulation (EU) 2017/745. If
              the integrating partner adds functions for diagnostic, therapeutic, preventive, or
              health status monitoring purposes to the KinetikOS outputs, their application
              constitutes an autonomous medical device, under the terms of Article 2(1) of
              Regulation (EU) 2017/745, subject to its own conformity assessment independent of
              KinetikOS.
            </p>
            <p>
              The revocation of any consent given does not affect the lawfulness of the processing
              and processing of personal data carried out before its withdrawal or revocation, nor
              the compliance with contractual or legal obligations applicable to Kinetikos.
            </p>
          </>
        ),
      },
      {
        heading: "Personal Data Retention Periods",
        body: (
          <>
            <p>
              Kinetikos processes and retains personal data only for the period strictly necessary
              for the provision of the service, the fulfillment of the purposes for which they
              were collected, and compliance with applicable legal and contractual obligations.
            </p>
            <p>
              Data processed by Kinetikos in the context of its role as a processor is retained
              during the term of the contractual agreement with the integrating partner, being
              deleted or anonymized upon termination of the contractual relationship, or earlier,
              whenever requested by the respective controller, unless there is a legal obligation
              to the contrary.
            </p>
            <p>
              The personal data of integrating partners' representatives are retained during the
              term of the contract and for the additional period necessary to comply with
              applicable legal obligations, namely regarding tax, accounting, and contractual
              liability matters.
            </p>
            <p>
              Audit and security logs are retained for the period necessary to demonstrate
              compliance with legal and contractual obligations, as well as to respond to any
              security incidents.
            </p>
            <p>
              Upon expiry of the applicable retention periods, personal data is securely deleted or
              irreversibly anonymized. Anonymized data, after being disassociated from any
              individual, may be used for statistical purposes and for research in biomechanics and
              movement sciences.
            </p>
          </>
        ),
      },
      {
        heading: "Communication and Sharing of Personal Data",
        body: (
          <>
            <p>
              Kinetikos uses subcontracted entities for the provision of technical services for
              infrastructure, cloud hosting, monitoring, maintenance, and security of the KinetikOS
              APIs. These entities process personal data on behalf and on account of Kinetikos,
              under subcontracting agreements that ensure compliance with the GDPR, namely through
              the imposition of confidentiality duties and the adoption of technical and
              organizational measures appropriate to the risk.
            </p>
            <p>
              Kinetikos does not sell, commercialize, or share personal data with third parties for
              purposes other than those described in this policy, without prejudice to compliance
              with applicable legal obligations.
            </p>
            <p>
              Some of the subcontracted entities have their own privacy and security policies, as
              well as internal procedures for the processing and handling of personal data, over
              which Kinetikos does not exercise any direct intervention. Kinetikos ensures, in any
              case, that the contractual commitments with these entities impose an adequate level
              of security and full compliance with the obligations of the GDPR.
            </p>
            <p>
              Kinetikos is not responsible for the content, links, or privacy policies published on
              the websites of subcontracted entities or integrating partners. In particular,
              Kinetikos is not responsible for the privacy and security policies, nor for the
              information security measures applied and implemented by integrating partners in the
              applications that integrate KinetikOS, and the end user must address their requests
              and concerns to the integrating partner in question, as the controller.
            </p>
            <p>
              Kinetikos does not transfer personal data to countries outside the European Union or
              the European Economic Area. Should an international transfer become necessary for
              duly justified operational reasons, it will be carried out in accordance with
              Articles 44 et seq. of the GDPR, with the appropriate guarantees provided therein
              and with prior information to the affected data subjects and partners.
            </p>
          </>
        ),
      },
      {
        heading: "Responsibility of Personal Data Subjects",
        body: (
          <>
            <p>
              Personal data subjects are obliged to comply with all rules and procedures provided
              in this privacy and security policy, as well as the applicable terms and conditions
              of use.
            </p>
            <p>
              Integrating partners and their respective representatives are obliged to fully comply
              with the contractual obligations, including, namely: (i) transmitting to KinetikOS
              only the data strictly necessary for the provision of the service, respecting the
              minimization principle; (ii) complying with the terms of use of the APIs; and (iii)
              ensuring, as controllers, the existence of a valid legal basis and adequate
              information to end users, under the terms of the GDPR.
            </p>
            <p>
              Kinetikos is not responsible for any direct or indirect damage or loss arising from
              non-compliance or violation of rules or procedures related to the use of KinetikOS,
              namely the undue, excessive, or unlawful transmission of data by integrating
              partners.
            </p>
          </>
        ),
      },
      {
        heading: "Rights of Personal Data Subjects",
        body: (
          <>
            <p>
              Personal data subjects may, at any time, free of charge, and in accordance with
              current legislation, exercise the following rights: right to information, access,
              rectification, erasure, data portability, restriction of processing, and objection to
              the processing of their personal data.
            </p>
            <p>
              <strong>Right to information</strong> — the data subject has the right to consult,
              always and at any time and place available and easily accessible, unconditionally and
              in an unconditional manner, all information about the scope of activity in which
              their personal data may be subject to any type of processing.
            </p>
            <p>
              <strong>Right of access</strong> — the data subject has the right to know whether
              their personal data is processed, or not, for the purposes provided for in this
              policy, the categories of data processed, the entities with whom the personal data
              may be shared, the retention periods, the origin of the data, and the methods by
              which they are collected, as well as the potential transfer to entities located
              outside the European Union.
            </p>
            <p>
              <strong>Right to rectification</strong> — the data subject has the right to proceed,
              at any time and without any associated cost, to the rectification of their personal
              data when they are inaccurate or outdated.
            </p>
            <p>
              <strong>Right to erasure</strong> — the data subject has the right to obtain, at any
              time and without any associated cost, the erasure of their personal data, under the
              terms of Article 17 of the GDPR, namely when the data are no longer necessary for
              the purpose for which they were collected, when the data subject withdraws consent,
              when the data has been processed unlawfully, or when the data subject objects to the
              processing.
            </p>
            <p>
              <strong>Right to restriction of processing</strong> — the data subject may request
              that the processing of their personal data be restricted under the terms of Article
              18 of the GDPR, namely when they contest the accuracy of the data, when the
              processing is unlawful, or when the data is no longer necessary for the processing
              purposes.
            </p>
            <p>
              <strong>Right to data portability</strong> — the data subject has the right to
              receive their data from the controller in a structured, commonly used, and
              machine-readable format, and has the same right to transmit this data to another
              controller, whenever the processing is based on consent or a contract and is carried
              out by automated means, under the terms of Article 20 of the GDPR.
            </p>
            <p>
              <strong>Right to object</strong> — the data subject may object to the processing of
              their personal data under the terms of Article 21 of the GDPR, namely when purposes
              different from those foreseen for a specific processing are involved, or when they
              are used for marketing and advertising purposes.
            </p>
            <p>
              Regarding the data of end users of integrating partners' applications, the rights
              described above must, as a rule, be exercised with the integrating partner, who is
              the controller. Kinetikos, in its capacity as a processor, will collaborate with the
              integrating partner in responding to data subjects' requests, under the terms
              contractually agreed upon and whenever applicable.
            </p>
            <p>
              All rights can be exercised at any time and free of charge, provided that the request
              is not manifestly unfounded or excessive; in which case, the responsible entity
              reserves the right to demand the payment of a fee to cover the associated
              administrative costs.
            </p>
            <p>Any of the rights described above can be exercised through the following means:</p>
            <ul>
              <li>Email to: data.protection@kinetikoshealth.com</li>
              <li>
                Registered postal mail with acknowledgment of receipt to: Rua Pedro Nunes, Edifício
                C, 3030-199 Coimbra, Portugal.
              </li>
            </ul>
            <p>
              Kinetikos undertakes to respond to the request as quickly as possible, without
              exceeding the legally provided deadlines, justifying and substantiating its decision,
              in accordance with applicable legislation.
            </p>
            <p>
              In any case, whenever the personal data subject considers that their rights have been
              violated or not safeguarded, they may file a formal complaint with the National Data
              Protection Commission (CNPD).
            </p>
          </>
        ),
      },
      {
        heading: "Protection and Security Measures",
        body: (
          <>
            <p>
              Kinetikos promotes all legally required security measures to ensure the privacy of
              collected and processed personal data, safeguarding information security in both
              online and offline environments.
            </p>
            <p>
              All communications with the KinetikOS APIs are carried out over the HTTPS protocol,
              with encryption in transit through the Transport Layer Security (TLS) protocol, in
              the currently supported version. This technology is used to improve the quality of
              personal data transmission over the internet, encrypting and protecting information
              considered sensitive and ensuring the integrating partner and end users that their
              data is not fraudulently intercepted, with all information processed with the maximum
              level of security.
            </p>
            <p>
              Similarly, cloud traffic, between processing instances and storage volumes, is
              encrypted. Storage servers are contained within a Virtual Private Cloud (VPC),
              inaccessible directly from the outside, not even by Kinetikos employees, without the
              mediation of access control mechanisms. All privileged access is maintained in an
              audit log.
            </p>
            <p>
              The database stores files in volumes encrypted with the AES-256 algorithm. Wherever
              applicable, logical separation between identifiers and measurement data is adopted,
              using pseudonymized codes generated by unidirectional and secure hash functions,
              stored in distinct repositories. This architecture ensures that unauthorized third
              parties cannot relate the data contained in different repositories, even if they
              access their content, totally or partially, ensuring the confidentiality and security
              of the processed data.
            </p>
            <p>
              Any interaction of a duly authorized user with the data, through the APIs, is
              recorded by the cloud service provider and can be consulted whenever necessary.
            </p>
            <p>
              Kinetikos also adopts vulnerability management controls, regular updating of software
              components, periodic review of access, and documented security incident response
              procedures, in accordance with industry best practices and applicable guides, namely
              EN 80001-1:2022 (application of risk management in IT networks with medical devices),
              IEC/TR 80001-2-2:2012 and IEC/TR 80001-2-8:2016.
            </p>
          </>
        ),
      },
      {
        heading: "Applicable Law",
        body: (
          <p>
            This privacy and security policy is drafted and fully subject to the legislation in
            force in the European Union, namely Regulation (EU) 2016/679, of April 27, 2016
            (General Data Protection Regulation - GDPR), and its respective national implementing
            law, particularly Law No. 58/2019, of August 8. Recommendations and guidelines issued
            by the European Data Protection Board and the National Data Protection Commission
            (CNPD) are also considered.
          </p>
        ),
      },
      {
        heading: "Changes to This Privacy and Security Policy",
        body: (
          <p>
            Kinetikos reserves the right to change this privacy and security policy, as well as any
            terms and conditions of the applicable sectoral policies, at any time, always in
            accordance with applicable national and Community legislation. The changes will be
            communicated to integrating partners and, when applicable, to personal data subjects,
            through the means contractually provided for or by publication on its institutional
            page.
          </p>
        ),
      },
    ],
  },

  pt: {
    title: "Política de Privacidade e de Segurança",
    subtitle: "KinetikOS",
    sections: [
      {
        heading: "Enquadramento",
        body: (
          <>
            <p>
              A KINETIKOS - DRIVEN SOLUTIONS, S.A. (adiante Kinetikos) é uma sociedade de direito
              português que desenvolveu o KinetikOS, um software baseado em nuvem que funciona como
              instrumento de medição biomecânica. O KinetikOS recebe sinais inerciais ou
              cinemáticos previamente adquiridos por sistemas de terceiros (unidades de medição
              inercial externas, IMUs de smartphones e smartwatches, sistemas optoelectrónicos e
              electromagnéticos laboratoriais) e, por meio da aplicação de algoritmos de
              processamento de sinais e do cálculo de descritores estatísticos, produz grandezas
              mecânicas descritivas do movimento humano, expressas em unidades físicas convencionais
              ou em descritores matemáticos adimensionais.
            </p>
            <p>
              Os parâmetros calculados pelo KinetikOS são disponibilizados, como dados técnicos de
              medição, exclusivamente por meio das APIs do KinetikOS a aplicações de terceiros que
              integrem o dispositivo, ao abrigo de acordo contratual ou de parceria técnica com o
              fabricante. A função do KinetikOS é meramente informativa e descritiva. O KinetikOS
              não realiza qualquer interpretação clínica, classificação, comparação com valores de
              referência, pontuação, alerta, recomendação ou juízo clínico sobre os valores
              calculados.
            </p>
            <p>
              O KinetikOS destina-se a investigadores em biomecânica e ciências do movimento,
              profissionais de ciências do desporto, profissionais de fitness, treinadores,
              utilizadores individuais em contextos de autorregisto e profissionais de saúde que o
              utilizem exclusivamente como instrumento de medição biomecânica.
            </p>
            <p>
              Dado que o KinetikOS é disponibilizado exclusivamente através de APIs a parceiros
              integradores, não existe, em regra, interação direta entre a Kinetikos e os
              utilizadores finais das aplicações de terceiros que integram o dispositivo. A
              recolha, o tratamento e a apresentação dos dados pessoais desses utilizadores finais
              são tipicamente da responsabilidade do parceiro integrador, como responsável pelo
              tratamento. No âmbito técnico da prestação do serviço, a Kinetikos pode aceder a
              dados técnicos de medição e a identificadores transmitidos pelo parceiro integrador
              por meio das APIs, atuando, nesses casos, como subcontratante do parceiro integrador,
              nos termos do artigo 28.º do Regulamento (UE) 2016/679 (RGPD).
            </p>
            <p>
              A presente política de privacidade e segurança visa informar todos os parceiros
              integradores, representantes destes e, na medida de que se aplique, utilizadores
              finais sobre como os dados pessoais eventualmente tratados no contexto do KinetikOS
              são recolhidos, tratados, conservados e protegidos, bem como sobre as finalidades e
              os prazos de conservação aplicáveis.
            </p>
          </>
        ),
      },
      {
        heading: "Entidades Responsáveis pelo Tratamento dos Seus Dados Pessoais",
        body: (
          <>
            <p>
              A entidade responsável pelo tratamento dos dados pessoais dos utilizadores finais das
              aplicações de terceiros que integram o KinetikOS é, em regra, o próprio parceiro
              integrador, cabendo-lhe definir as finalidades e os meios de tratamento desses dados
              junto dos utilizadores finais, bem como assegurar o cumprimento das obrigações
              decorrentes do RGPD, designadamente em matéria de bases de licitude, informação,
              segurança e exercício de direitos.
            </p>
            <p>
              No âmbito da prestação do serviço de medição biomecânica por meio das APIs do
              KinetikOS, a Kinetikos atua como subcontratante do parceiro integrador, recebendo e
              processando os sinais e parâmetros transmitidos, exclusivamente ao abrigo de um
              acordo contratual que define os termos do tratamento, em conformidade com o artigo
              28.º do RGPD.
            </p>
            <p>
              No que respeita aos dados pessoais de representantes, utilizadores técnicos e pessoas
              de contacto dos parceiros integradores (nomeadamente os dados recolhidos para efeitos
              de registo, provisionamento de credenciais de API, faturação, suporte e comunicação
              comercial), a entidade responsável pelo tratamento é a:
            </p>
            <p>
              <strong>KINETIKOS - DRIVEN SOLUTIONS, S.A.</strong>
              <br />
              Morada: Rua Pedro Nunes, Edifício C, 3030-199 Coimbra, Portugal
              <br />
              Email: info@kinetikoshealth.com
              <br />
              Telefone: (+351) 215 836 938
            </p>
          </>
        ),
      },
      {
        heading: "Que Dados Pessoais São Recolhidos",
        body: (
          <>
            <p>
              No contexto da sua atuação como subcontratante do parceiro integrador, a Kinetikos
              recebe, através das APIs do KinetikOS, os dados transmitidos por aquele. A natureza
              e o âmbito dos dados recebidos são definidos, caso a caso, no acordo contratual
              celebrado com cada parceiro integrador, sendo limitados ao estritamente necessário à
              prestação do serviço de medição. Podem incluir, nomeadamente:
            </p>
            <ul>
              <li>
                Identificadores pseudonimizados ou técnicos atribuídos pelo parceiro integrador aos
                seus utilizadores finais (por exemplo, identificadores de sessão ou identificadores
                internos de utilizador), sem que a Kinetikos tenha, em regra, acesso aos dados
                diretos de identificação desses utilizadores.
              </li>
              <li>
                Sinais inerciais ou cinemáticos previamente adquiridos por sistemas de terceiros,
                que, consoante o caso e a sua associação a uma pessoa identificada ou
                identificável, podem constituir dados biométricos na aceção do RGPD.
              </li>
              <li>
                Parâmetros antropométricos técnicos (altura, peso, idade e sexo), quando
                transmitidos pelo parceiro integrador, exclusivamente na medida necessária à
                aplicação dos modelos matemáticos de normalização dimensional, à estimativa da
                inércia de segmentos corporais e a outros cálculos biomecânicos. Estes parâmetros
                são tratados como entradas técnicas dos algoritmos e não para fins de
                caracterização demográfica, de comparação com valores normativos, de classificação,
                de pontuação ou de qualquer interpretação clínica.
              </li>
              <li>
                Metadados técnicos associados aos pedidos de API, designadamente timestamps, tipo
                de sinal, parâmetros de configuração e identificador do parceiro integrador.
              </li>
            </ul>
            <p>
              A Kinetikos não recolhe nem requer dados diretos de identificação dos utilizadores
              finais (como nome, data de nascimento, morada ou contactos), dados clínicos, dados de
              georreferenciação, dados de câmara, dados de microfone ou quaisquer outros dados não
              estritamente necessários ao cálculo das grandezas mecânicas descritivas do movimento.
              O KinetikOS não acede a sensores, à câmara ou à localização do dispositivo do
              utilizador final.
            </p>
            <p>
              No contexto da sua atuação como responsável pelo tratamento, no âmbito da relação
              contratual com os parceiros integradores, a Kinetikos recolhe os seguintes dados
              pessoais dos respetivos representantes e utilizadores técnicos:
            </p>
            <ul>
              <li>
                Dados de identificação e contacto - nome, cargo, endereço de correio eletrónico e,
                opcionalmente, número de telefone.
              </li>
              <li>
                Dados de autenticação e utilização das APIs - credenciais técnicas, registos de
                acesso e de auditoria.
              </li>
              <li>
                Dados contratuais e de faturação - informações necessárias à celebração e à
                execução do contrato de prestação de serviços.
              </li>
            </ul>
            <p>
              Os dados pessoais recolhidos no âmbito do KinetikOS são os adequados, pertinentes e
              limitados ao necessário ao cumprimento das finalidades de tratamento, em conformidade
              com o princípio da minimização dos dados consagrado no artigo 5.º, n.º 1, alínea c),
              do RGPD.
            </p>
          </>
        ),
      },
      {
        heading: "Como Recolhemos e Acedemos aos Dados Pessoais",
        body: (
          <>
            <p>
              Os dados tratados no âmbito da prestação do serviço de medição são recebidos pelo
              KinetikOS exclusivamente através das suas APIs, por iniciativa e sob controlo do
              parceiro integrador. A Kinetikos não capta, regista nem adquire dados diretamente
              dos utilizadores finais.
            </p>
            <p>
              Os dados pessoais dos representantes dos parceiros integradores são recolhidos
              diretamente pela Kinetikos no momento do registo, do provisionamento das credenciais
              de API ou no âmbito do processo contratual, por meio do preenchimento de formulários
              ou de comunicações formais entre as partes.
            </p>
          </>
        ),
      },
      {
        heading: "Para Que Finalidades São Tratados os Dados Pessoais",
        body: (
          <>
            <p>
              Os dados pessoais tratados no âmbito do KinetikOS são tratados estritamente em
              conformidade com o acordo contratual celebrado com o parceiro integrador, para as
              seguintes finalidades:
            </p>
            <ul>
              <li>
                Prestação do serviço de medição biomecânica, que consiste no processamento de
                sinais inerciais ou cinemáticos e na produção de parâmetros descritivos do
                movimento humano, a ser disponibilizada exclusivamente ao parceiro integrador por
                meio das APIs do KinetikOS.
              </li>
              <li>
                Cumprimento das obrigações contratuais e legais associadas à prestação do serviço,
                incluindo gestão de acessos, registo de auditoria, gestão de segurança, faturação e
                suporte técnico.
              </li>
              <li>
                Gestão técnica e operacional das APIs, incluindo a identificação e resolução de
                falhas, o controlo do desempenho e a monitorização da segurança, sempre que
                tecnicamente necessário.
              </li>
              <li>
                Gestão de conteúdos, que consiste na recolha de dados pessoais, mediante
                consentimento expresso e informado do destinatário, para a receção de newsletters
                com as novidades registadas na atividade desenvolvida pela Kinetikos e nos demais
                serviços por esta prestados.
              </li>
            </ul>
            <p>
              A Kinetikos não interpreta, classifica, compara com valores de referência, pontua,
              alerta, recomenda ou emite qualquer juízo clínico sobre os dados tratados. Tais
              atividades, caso sejam realizadas pelo parceiro integrador ou por terceiros, são de
              sua exclusiva responsabilidade e constituem utilização a jusante, fora do fim a que
              se destina, declarado pelo fabricante, nos termos do artigo 2.º, n.º 12, do
              Regulamento (UE) 2017/745. Caso o parceiro integrador adicione funções com finalidade
              diagnóstica, terapêutica, preventiva ou de monitorização de estados de saúde às
              saídas do KinetikOS, a sua aplicação constitui um dispositivo médico autónomo, nos
              termos do artigo 2.º, n.º 1, do Regulamento (UE) 2017/745, sujeito a avaliação de
              conformidade própria e independente do KinetikOS.
            </p>
            <p>
              A revogação de qualquer consentimento prestado não afeta a licitude do tratamento e
              do processamento de dados pessoais realizados antes de sua retirada ou revogação, nem
              o cumprimento de obrigações contratuais ou legais aplicáveis à Kinetikos.
            </p>
          </>
        ),
      },
      {
        heading: "Prazos de Conservação dos Dados Pessoais",
        body: (
          <>
            <p>
              A Kinetikos trata e conserva os dados pessoais apenas pelo período estritamente
              necessário à prestação do serviço, ao cumprimento das finalidades para as quais foram
              recolhidos e às obrigações legais e contratuais aplicáveis.
            </p>
            <p>
              Os dados tratados pela Kinetikos no contexto da sua atuação como subcontratante são
              conservados durante a vigência do acordo contratual com o parceiro integrador, sendo
              eliminados ou anonimizados à cessação da relação contratual, ou antes, sempre que tal
              seja solicitado pelo respetivo responsável pelo tratamento, salvo obrigação legal em
              contrário.
            </p>
            <p>
              Os dados pessoais dos representantes dos parceiros integradores são conservados
              durante a vigência do contrato e pelo período adicional necessário ao cumprimento das
              obrigações legais aplicáveis, nomeadamente em matéria fiscal, contabilística e de
              responsabilidade contratual.
            </p>
            <p>
              Os registos de auditoria e de segurança são conservados pelo período necessário à
              demonstração do cumprimento das obrigações legais e contratuais, bem como à resposta
              a eventuais incidentes de segurança.
            </p>
            <p>
              Findos os prazos de conservação aplicáveis, os dados pessoais são eliminados de forma
              segura ou anonimizados de forma irreversível. Os dados anonimizados, após
              desassociados de qualquer pessoa singular, podem ser utilizados para fins
              estatísticos e para investigação em biomecânica e ciências do movimento.
            </p>
          </>
        ),
      },
      {
        heading: "Comunicação e Partilha de Dados Pessoais",
        body: (
          <>
            <p>
              A Kinetikos recorre a entidades subcontratadas para a prestação de serviços técnicos
              de infraestrutura, alojamento em nuvem, monitorização, manutenção e segurança das
              APIs do KinetikOS. Estas entidades procedem ao tratamento dos dados pessoais em nome
              e por conta da Kinetikos, ao abrigo de contratos de subcontratação que asseguram o
              cumprimento do RGPD, designadamente por meio da imposição de deveres de
              confidencialidade e da adoção de medidas técnicas e organizativas adequadas ao risco.
            </p>
            <p>
              A Kinetikos não procede à venda, à comercialização ou à partilha de dados pessoais
              com terceiros para finalidades distintas das descritas na presente política, sem
              prejuízo do cumprimento das obrigações legais aplicáveis.
            </p>
            <p>
              Algumas das entidades subcontratadas detêm políticas de privacidade e segurança
              próprias, assim como procedimentos internos para o tratamento e o processamento de
              dados pessoais, sobre os quais a Kinetikos não exerce qualquer intervenção direta. A
              Kinetikos assegura, em todo o caso, que os compromissos contratuais com essas
              entidades impõem um nível de segurança adequado e o cumprimento integral das
              obrigações do RGPD.
            </p>
            <p>
              A Kinetikos não é responsável pelos conteúdos, ligações ou políticas de privacidade
              publicadas em sítios de internet de entidades subcontratadas ou de parceiros
              integradores. Em particular, a Kinetikos não é responsável pelas políticas de
              privacidade e de segurança, nem pelas medidas de segurança da informação aplicadas e
              implementadas pelos parceiros integradores nas aplicações que integram o KinetikOS,
              devendo o utilizador final dirigir os seus pedidos e preocupações ao parceiro
              integrador em causa, enquanto responsável pelo tratamento.
            </p>
            <p>
              A Kinetikos não procede à transferência de dados pessoais para países situados fora
              da União Europeia ou do Espaço Económico Europeu. Caso, por razões operacionais
              devidamente fundamentadas, venha a ser necessária uma transferência internacional, a
              mesma será realizada em conformidade com os artigos 44.º e seguintes do RGPD,
              mediante as garantias adequadas aí previstas e com informação prévia aos titulares e
              parceiros afetados.
            </p>
          </>
        ),
      },
      {
        heading: "Responsabilidade dos Titulares de Dados Pessoais",
        body: (
          <>
            <p>
              Os titulares de dados pessoais obrigam-se a cumprir todas as regras e procedimentos
              previstos na presente política de privacidade e segurança, bem como nos termos e
              condições de utilização aplicáveis.
            </p>
            <p>
              Os parceiros integradores e os respetivos representantes obrigam-se a cumprir
              integralmente as obrigações contratuais, incluindo, designadamente: (i) transmitir
              ao KinetikOS apenas os dados estritamente necessários à prestação do serviço, no
              respeito do princípio da minimização; (ii) cumprir os termos de utilização das APIs;
              e (iii) assegurar, enquanto responsáveis pelo tratamento, a existência de base de
              licitude válida e de informação adequada aos utilizadores finais, nos termos do
              RGPD.
            </p>
            <p>
              A Kinetikos não se responsabiliza por quaisquer danos ou prejuízos emergentes, direta
              ou indiretamente, decorrentes do incumprimento ou da violação de regras ou
              procedimentos relativos à utilização do KinetikOS, designadamente a transmissão
              indevida, excessiva ou ilícita de dados por parte dos parceiros integradores.
            </p>
          </>
        ),
      },
      {
        heading: "Direitos dos Titulares de Dados Pessoais",
        body: (
          <>
            <p>
              Os titulares de dados pessoais podem, em qualquer momento, gratuitamente e de acordo
              com a legislação em vigor, exercer os seguintes direitos: direito de informação,
              acesso, retificação, apagamento, portabilidade, limitação e oposição ao tratamento
              dos seus dados pessoais.
            </p>
            <p>
              <strong>Direito à informação</strong> — o titular tem direito a consultar, sempre e
              em qualquer momento e local disponíveis e de fácil acesso, incondicionado e de forma
              incondicionada, toda a informação sobre o âmbito de atividade no qual os seus dados
              pessoais possam ser objeto de qualquer tipo de tratamento.
            </p>
            <p>
              <strong>Direito de acesso</strong> — o titular tem direito a saber se os seus dados
              pessoais são tratados, ou não, para as finalidades previstas na presente política, as
              categorias de dados tratados, as entidades para as quais os dados pessoais possam
              ser partilhados, os prazos de conservação, a origem dos dados e os métodos pelos
              quais são recolhidos, bem como a eventual transferência para entidades localizadas
              fora da União Europeia.
            </p>
            <p>
              <strong>Direito de retificação</strong> — o titular tem direito a proceder, a
              qualquer momento e sem qualquer custo associado, à retificação dos seus dados
              pessoais quando estes sejam inexatos ou desatualizados.
            </p>
            <p>
              <strong>Direito ao apagamento</strong> — o titular tem direito a obter, a qualquer
              momento e sem qualquer custo associado, o apagamento dos seus dados pessoais, nos
              termos do artigo 17.º do RGPD, designadamente quando os dados deixem de ser
              necessários para a finalidade para a qual foram recolhidos, quando o titular retire o
              consentimento, quando os dados tenham sido tratados ilicitamente ou quando o titular
              se oponha ao tratamento.
            </p>
            <p>
              <strong>Direito à limitação do tratamento</strong> — o titular pode requerer que o
              tratamento dos seus dados pessoais fique limitado nos termos do artigo 18.º do RGPD,
              designadamente quando conteste a exatidão dos dados, quando o tratamento seja ilícito
              ou quando os dados já não sejam necessários às finalidades de tratamento.
            </p>
            <p>
              <strong>Direito à portabilidade dos dados</strong> — o titular tem direito a receber
              do responsável pelo tratamento os seus dados num formato estruturado, de uso corrente
              e de leitura automática, tendo igual direito a transmitir esses dados a outra
              entidade responsável, sempre que o tratamento se baseie em consentimento ou em
              contrato e seja realizado por meios automatizados, nos termos do artigo 20.º do RGPD.
            </p>
            <p>
              <strong>Direito de oposição</strong> — o titular pode opor-se ao tratamento dos seus
              dados pessoais nos termos do artigo 21.º do RGPD, designadamente quando estiverem em
              causa finalidades diferentes das previstas para determinado tratamento, ou quando
              sejam utilizados para efeitos de marketing e publicidade.
            </p>
            <p>
              No que respeita aos dados dos utilizadores finais de aplicações de parceiros
              integradores, os direitos acima descritos devem, em regra, ser exercidos junto do
              parceiro integrador, responsável pelo tratamento. A Kinetikos, na qualidade de
              subcontratante, colaborará com o parceiro integrador na resposta aos pedidos dos
              titulares, nos termos contratualmente acordados e sempre que aplicável.
            </p>
            <p>
              Todos os direitos podem ser exercidos a qualquer momento e de forma gratuita, desde
              que o pedido não se revele manifestamente infundado ou excessivo; caso em que, a
              entidade responsável, se reserva o direito de exigir o pagamento de uma taxa para
              cobrir os custos administrativos associados.
            </p>
            <p>Qualquer dos direitos acima descritos pode ser exercido através dos seguintes meios:</p>
            <ul>
              <li>Correio eletrónico para: data.protection@kinetikoshealth.com</li>
              <li>
                Correio postal registado com aviso de receção para: Rua Pedro Nunes, Edifício C,
                3030-199 Coimbra, Portugal.
              </li>
            </ul>
            <p>
              A Kinetikos compromete-se a responder ao pedido no mais curto prazo possível, sem
              exceder os prazos legalmente previstos, justificando e fundamentando a sua decisão,
              nos termos da legislação aplicável.
            </p>
            <p>
              Em qualquer caso, sempre que o titular de dados pessoais considere que os seus
              direitos foram violados ou não salvaguardados, pode apresentar queixa formal perante
              a Comissão Nacional de Proteção de Dados (CNPD).
            </p>
          </>
        ),
      },
      {
        heading: "Medidas de Proteção e de Segurança",
        body: (
          <>
            <p>
              A Kinetikos promove todas as medidas de segurança legalmente exigidas, a fim de
              garantir a privacidade dos dados pessoais recolhidos e tratados, salvaguardando a
              segurança da informação tanto em ambiente online quanto offline.
            </p>
            <p>
              Todas as comunicações com as APIs do KinetikOS são realizadas sobre o protocolo
              HTTPS, com encriptação em trânsito por meio do protocolo Transport Layer Security
              (TLS), na versão atualmente suportada. Esta tecnologia é utilizada para melhorar a
              qualidade da transmissão de dados pessoais através da internet, encriptando e
              protegendo a informação considerada de caráter sensível e garantindo ao parceiro
              integrador e aos utilizadores finais que os seus dados não são intercetados de forma
              fraudulenta, sendo toda a informação tratada com o nível máximo de segurança.
            </p>
            <p>
              Da mesma forma, o tráfego na nuvem, entre as instâncias de processamento e os
              volumes de armazenamento é encriptado. Os servidores de armazenamento encontram-se
              contidos numa rede privada virtual (VPC - Virtual Private Cloud), inacessíveis
              diretamente a partir do exterior, nem mesmo pelos funcionários da Kinetikos, sem
              mediação dos mecanismos de controlo de acesso. Todos os acessos privilegiados são
              mantidos em registo de auditoria.
            </p>
            <p>
              A base de dados armazena os ficheiros em volumes encriptados com o algoritmo AES-256.
              Sempre que aplicável, adota-se a separação lógica entre identificadores e dados de
              medição, por meio de códigos pseudonimizados gerados por funções de hash
              unidirecionais e seguras, armazenados em repositórios distintos. Esta arquitetura
              garante que terceiros não autorizados não possam relacionar os dados contidos em
              diferentes repositórios, mesmo que acedam, total ou parcialmente, ao seu conteúdo,
              assegurando a confidencialidade e a segurança dos dados tratados.
            </p>
            <p>
              Qualquer interação de um utilizador devidamente autorizado com os dados, através das
              APIs, é registada pelo fornecedor de serviços em nuvem e pode ser consultada sempre
              que necessário.
            </p>
            <p>
              A Kinetikos adota, ainda, controlos de gestão de vulnerabilidades, atualização
              regular dos componentes de software, revisão periódica dos acessos e procedimentos
              documentados de resposta a incidentes de segurança, em conformidade com as melhores
              práticas da indústria e com os guias aplicáveis, designadamente EN 80001-1:2022
              (aplicação da gestão de risco em redes de TI com dispositivos médicos), IEC/TR
              80001-2-2:2012 e IEC/TR 80001-2-8:2016.
            </p>
          </>
        ),
      },
      {
        heading: "Lei Aplicável",
        body: (
          <p>
            A presente política de privacidade e segurança encontra-se elaborada e integralmente
            sujeita à legislação vigente na União Europeia, designadamente ao Regulamento (UE)
            2016/679, de 27 de abril de 2016 (Regulamento Geral de Proteção de Dados - RGPD), e à
            respetiva lei nacional de execução, em particular à Lei n.º 58/2019, de 8 de agosto.
            São igualmente consideradas as recomendações e orientações emanadas do Comité Europeu
            de Proteção de Dados e da Comissão Nacional de Proteção de Dados (CNPD).
          </p>
        ),
      },
      {
        heading: "Alterações à Presente Política de Privacidade e de Segurança",
        body: (
          <p>
            A Kinetikos reserva-se o direito de alterar, a qualquer momento, a presente política de
            privacidade e segurança, bem como quaisquer termos e condições das políticas sectoriais
            aplicáveis, sempre em conformidade com a legislação nacional e comunitária aplicável.
            As alterações serão comunicadas aos parceiros integradores e, quando aplicável, aos
            titulares de dados pessoais, pelos meios contratualmente previstos ou por meio de
            publicação em sua página institucional.
          </p>
        ),
      },
    ],
  },
};
